<template>
  <div class="order-dialog" role="dialog" aria-modal="true" :aria-label="title">
    <div class="order-dialog__overlay" @click="$emit('cancel')" aria-hidden="true"></div>
    <form class="order-dialog__form" @submit.prevent="$emit('submit')">
      <div class="order-dialog__backdrop"></div>
      <div class="order-dialog__card">
        <div class="order-dialog__header">
          <h1 class="order-dialog__title">{{ title }}</h1>
        </div>

        <div class="order-dialog__body">
          <div class="order-dialog__grid">
            <input
              type="text"
              :value="firstName"
              @input="$emit('update:firstName', $event.target.value)"
              placeholder="First Name"
              required
              class="order-dialog__input"
            />
            <input
              type="text"
              :value="lastName"
              @input="$emit('update:lastName', $event.target.value)"
              placeholder="Last Name"
              required
              class="order-dialog__input"
            />
          </div>

          <div class="order-dialog__grid">
            <input
              type="email"
              :value="email"
              @input="$emit('update:email', $event.target.value)"
              placeholder="Email Address"
              required
              class="order-dialog__input"
            />
            <input
              type="tel"
              :value="cell"
              @input="$emit('update:cell', $event.target.value)"
              placeholder="Cell Number (+27 12 345 6789)"
              required
              class="order-dialog__input"
            />
          </div>

          <div>
            <textarea
              :value="address"
              @input="$emit('update:address', $event.target.value)"
              placeholder="Full Address (Street, City, Postal Code)"
              rows="2"
              required
              class="order-dialog__textarea"
            ></textarea>
          </div>

        <fieldset class="order-dialog__shipping">
          <legend class="order-dialog__shipping-title">Delivery Method</legend>
          <div class="order-dialog__shipping-options">
            <label
              v-for="option in shippingOptions"
              :key="option.value"
              class="order-dialog__shipping-option"
            >
              <input
                type="radio"
                :value="option.value"
                :checked="shippingOption === option.value"
                @change="$emit('update:shippingOption', option.value)"
              />
              <div class="order-dialog__shipping-content">
                <span class="order-dialog__shipping-name">{{ option.label }}</span>
                <span class="order-dialog__shipping-meta">{{ option.description }}</span>
              </div>
              <span class="order-dialog__shipping-price">
                {{ currencyPrefix }}{{ displayPrice(option.price) }}
              </span>
            </label>
          </div>
        </fieldset>

          <div class="order-dialog__section">
            <h2 class="order-dialog__subtitle">Order Summary</h2>
            <div v-if="orderItems.length" class="order-dialog__summary">
              <div
                v-for="(item, index) in orderItems"
                :key="itemKey(item, index)"
                class="order-dialog__summary-item"
              >
                <span class="order-dialog__summary-name">{{ getItemName(item) }}</span>
                <span class="order-dialog__summary-qty">Quantity: {{ item.quantity }}</span>
                <span class="order-dialog__summary-price">
                  {{ currencyPrefix }}{{ displayPrice(getItemPrice(item) * item.quantity) }}
                </span>
              </div>
            </div>
            <p v-else class="order-dialog__summary-empty">
              <slot name="empty">Your cart is empty. Add products before checking out.</slot>
            </p>
            <p v-if="orderItems.length" class="order-dialog__summary-hint">
              Need to edit items? Close this form and adjust the cart drawer first.
            </p>
          </div>

          <div class="order-dialog__shipping-summary" v-if="shippingCost">
            <span>Delivery</span>
            <span>{{ currencyPrefix }}{{ displayPrice(shippingCost) }}</span>
          </div>

          <div class="order-dialog__total">
            <p>
              {{ totalLabel }}: {{ currencyPrefix }}{{ displayPrice(orderTotal) }}
            </p>
          </div>
        </div>

        <div class="order-dialog__actions">
          <button type="button" class="order-dialog__cancel" @click="$emit('cancel')">
            {{ cancelLabel }}
          </button>
          <button
            type="submit"
            class="order-dialog__submit"
            :disabled="submitting"
          >
            <svg
              v-if="submitting"
              class="order-dialog__spinner"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ submitting ? processingLabel : submitLabel }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'OrderDetailsDialog',
  props: {
    title: {
      type: String,
      default: 'Enter Your Order Details'
    },
    firstName: {
      type: String,
      default: ''
    },
    lastName: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    },
    cell: {
      type: String,
      default: ''
    },
    address: {
      type: String,
      default: ''
    },
    orderItems: {
      type: Array,
      default: () => []
    },
    orderTotal: {
      type: Number,
      default: 0
    },
    submitting: {
      type: Boolean,
      default: false
    },
    formatPrice: {
      type: Function,
      default: value => {
        if (typeof value === 'number') {
          return value.toLocaleString('en-ZA')
        }
        const numeric = parseFloat(String(value).replace(/[^\d.]/g, ''))
        return Number.isNaN(numeric) ? '0' : numeric.toLocaleString('en-ZA')
      }
    },
    getItemPrice: {
      type: Function,
      default: item => item?.price || 0
    },
    getItemName: {
      type: Function,
      default: item => item?.product || ''
    },
    shippingOptions: {
      type: Array,
      default: () => [
        { value: 'standard', label: 'Standard Delivery', description: '2-3 days', price: 150 },
        { value: 'express', label: 'Express Delivery', description: '1-2 days', price: 250 }
      ]
    },
    shippingOption: {
      type: String,
      default: 'standard'
    },
    shippingCost: {
      type: Number,
      default: 0
    },
    currencyPrefix: {
      type: String,
      default: 'R'
    },
    totalLabel: {
      type: String,
      default: 'Order Total'
    },
    cancelLabel: {
      type: String,
      default: 'Cancel'
    },
    submitLabel: {
      type: String,
      default: 'Send Order'
    },
    processingLabel: {
      type: String,
      default: 'Processing...'
    }
  },
  emits: [
    'cancel',
    'submit',
    'update:firstName',
    'update:lastName',
    'update:email',
    'update:cell',
    'update:address',
    'update:shippingOption'
  ],
  methods: {
    itemKey(item, index) {
      const key = item?.product || item?.name
      return key ? `${key}-${index}` : index
    },
    displayPrice(value) {
      return this.formatPrice(value)
    }
  }
}
</script>

<style scoped>
.order-dialog {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 70;
}

.order-dialog__overlay {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(6px);
}

.order-dialog__form {
  position: relative;
  width: 100%;
  max-width: 640px;
}

.order-dialog__backdrop {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #52cd36, #3da026);
  transform: translateY(20px) scale(0.98);
  filter: blur(18px);
  border-radius: 30px;
}

.order-dialog__card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #52cd36;
  color: #ffffff;
  padding: clamp(2rem, 6vw, 3.5rem);
  border-radius: 30px;
  box-shadow: 0 35px 70px rgba(18, 48, 22, 0.4);
  max-height: 85vh;
  overflow: hidden;
}

.order-dialog__header {
  text-align: center;
  padding-bottom: 1.5rem;
}

.order-dialog__title {
  font-family: 'Montserrat', sans-serif;
  font-size: 2rem;
  font-weight: 700;
}

.order-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-bottom: 1.5rem;
}

.order-dialog__body::-webkit-scrollbar {
  width: 0.4rem;
}

.order-dialog__body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 9999px;
}

.order-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-bottom: 1.5rem;
}

.order-dialog__body::-webkit-scrollbar {
  width: 0.4rem;
}

.order-dialog__body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 9999px;
}

.order-dialog__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

@media (min-width: 768px) {
  .order-dialog__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.order-dialog__input,
.order-dialog__textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 14px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #111827;
  font-size: 0.95rem;
}

.order-dialog__textarea {
  min-height: 110px;
  resize: vertical;
}

.order-dialog__shipping {
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.order-dialog__shipping-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.order-dialog__shipping-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.order-dialog__shipping-option {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.order-dialog__shipping-option input {
  width: 1.1rem;
  height: 1.1rem;
  accent-color: #0f1d12;
}

.order-dialog__shipping-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.order-dialog__shipping-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.order-dialog__shipping-meta {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

.order-dialog__shipping-price {
  font-weight: 600;
  font-size: 0.95rem;
}

.order-dialog__section {
  margin-bottom: 1.5rem;
}

.order-dialog__subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0.75rem;
}

.order-dialog__summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.order-dialog__summary-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.9rem 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f1d12;
}

.order-dialog__summary-name {
  font-weight: 600;
  font-size: 1rem;
}

.order-dialog__summary-qty {
  font-weight: 600;
}

.order-dialog__summary-price {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
}

.order-dialog__summary-empty,
.order-dialog__summary-hint {
  font-size: 0.9rem;
  opacity: 0.85;
}

.order-dialog__summary-hint {
  margin-top: 0.75rem;
}

.order-dialog__shipping-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.order-dialog__total {
  text-align: center;
  margin-bottom: 1.5rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
}

.order-dialog__actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.order-dialog__cancel,
.order-dialog__submit {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 9999px;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.order-dialog__cancel {
  background: rgba(255, 255, 255, 0.85);
  color: #0f1d12;
}

.order-dialog__submit {
  background: #0f1d12;
  color: #ffffff;
  gap: 0.5rem;
}

.order-dialog__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.order-dialog__spinner {
  animation: spin 1s linear infinite;
  height: 1.25rem;
  width: 1.25rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .order-dialog {
    padding: 1rem;
  }

  .order-dialog__card {
    padding: 2rem 1.5rem;
  }

  .order-dialog__actions {
    flex-direction: column;
  }
}
</style>

